/**
 * Resolves a user-entered help video link into something the preview can render.
 *
 * Accepted links:
 * - YouTube (`youtube.com/watch?v=`, `youtu.be/`, `/embed/`, `/shorts/`, `/live/`)
 * - Vimeo (`vimeo.com/<id>`, `player.vimeo.com/video/<id>`)
 * - Direct video files (`.mp4`, `.mov`, `.webm`, `.ogg`, `.ogv`, `.m4v`)
 *
 * Anything else returns `null` and is treated as "not a video".
 */
export type HelpVideoPreview =
  | { kind: 'embed'; provider: 'youtube' | 'vimeo'; videoId: string; embedUrl: string }
  | { kind: 'file'; fileUrl: string };

const VIDEO_FILE_EXTENSION = /\.(mp4|mov|webm|ogg|ogv|m4v)$/i;
const YOUTUBE_ID = /^[\w-]{11}$/;

export function parseHelpVideoUrl(raw: string): HelpVideoPreview | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

  const host = url.hostname.replace(/^(www\.|m\.)/, '');

  const youtubeId = getYoutubeId(host, url);
  if (youtubeId) {
    return {
      kind: 'embed',
      provider: 'youtube',
      videoId: youtubeId,
      embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
    };
  }

  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const vimeoId = url.pathname.match(/(?:^|\/)(\d+)(?:\/|$)/)?.[1];
    if (vimeoId) {
      return {
        kind: 'embed',
        provider: 'vimeo',
        videoId: vimeoId,
        embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
      };
    }
  }

  if (VIDEO_FILE_EXTENSION.test(url.pathname)) {
    return { kind: 'file', fileUrl: url.href };
  }

  return null;
}

function getYoutubeId(host: string, url: URL): string | null {
  let id: string | null = null;
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    id =
      url.pathname === '/watch'
        ? url.searchParams.get('v')
        : (url.pathname.match(/^\/(?:embed|shorts|live)\/([^/]+)/)?.[1] ?? null);
  } else if (host === 'youtu.be') {
    id = url.pathname.split('/')[1] || null;
  }
  return id && YOUTUBE_ID.test(id) ? id : null;
}

/**
 * Static thumbnail for a YouTube video. Vimeo has no static URL pattern — its thumbnail has to be
 * looked up (see `fetchVimeoThumbnailUrl`).
 */
export function getYoutubeThumbnailUrl(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

/**
 * Looks up a Vimeo video's thumbnail through Vimeo's public oEmbed endpoint.
 * Uses `fetch` (not HttpClient) so the app's auth interceptor never attaches tokens to a
 * third-party request. Resolves to `null` when the video is private or the lookup fails.
 */
export async function fetchVimeoThumbnailUrl(videoId: string): Promise<string | null> {
  try {
    const target = encodeURIComponent(`https://vimeo.com/${videoId}`);
    const res = await fetch(`https://vimeo.com/api/oembed.json?url=${target}&width=640`);
    if (!res.ok) return null;
    const body = (await res.json()) as { thumbnail_url?: string };
    return body.thumbnail_url ?? null;
  } catch {
    return null;
  }
}
